import { Button, LinearProgress } from '@tunasong/ui-lib'
import type { HTMLAttributes } from 'react'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { Note } from '@tunasong/models'
import { matchAny, noteTemplate, shortUuid } from '@tunasong/models'
import { usePlugins } from '@tunasong/plugin-lib'
import { useCurrentUser, useMimeTypes } from '@tunasong/redux'
import { isMusicXML, isPdf } from '@tunasong/schemas'
import type { Persisted } from '@tunasong/schemas'
import { createCollaborativeDoc } from '@tunasong/sync-lib'
import type { SidebarComponent } from '@tunasong/ui-lib'
import { Divider, EntityContent, Typography, UploadStoredEntity, VBox } from '@tunasong/ui-lib'
import { Fragment, useCallback, useState, type FC } from 'react'
import { useEditorClass } from '../hooks/index.js'
import styles from './entity-notes.module.css'

const contentFilter = matchAny(isPdf, isMusicXML)

const NoteEditorBox = (props: HTMLAttributes<HTMLDivElement>) => (
  <div className={styles.noteEditorBox}>{props.children}</div>
)

export const EntityNotes: FC<SidebarComponent> = ({ entity, style: styleProp }) => {
  const { entities: notes, isSuccess } = graphHooks.useEntityChildren<Note>({ parentId: entity?.id, filter: 'note' })
  const { userId } = useCurrentUser()

  const { createEntity } = graphHooks.useEntityCreate<Note>()
  const [createdNote, setCreatedNote] = useState<Persisted<Note> | null>(null)

  const handleCreateNote = useCallback(async () => {
    const id = shortUuid()
    const { yDoc } = createCollaborativeDoc({ id, nodes: [{ type: 'line', children: [{ text: '' }] }] })
    const draft = { ...noteTemplate({ id, parentId: entity.id, userId }), yDoc } as Note
    const persisted = await createEntity({ entity: draft, parent: entity, isPrivate: true })
    setCreatedNote(persisted as Persisted<Note>)
  }, [createEntity, entity, userId])

  const { entities, isLoading } = graphHooks.useEntityChildren({
    parentId: entity?.id,
    filter: contentFilter,
  })
  const [deleteEntity, { isLoading: isDeleting }] = graphHooks.useEntityDelete()

  const NoteEditor = useEditorClass('NoteEditor')
  const { mimeTypeAccept } = useMimeTypes()

  const plugins = usePlugins('all') ?? []

  const filteredNotes = createdNote
    ? notes.filter(n => n.id !== createdNote.id)
    : notes
  const hasNote = isSuccess ? filteredNotes.length > 0 || createdNote !== null : undefined

  return (
    <VBox style={{ flex: 1, ...styleProp }}>
      <Divider>
        <Typography variant="caption">Personal Note</Typography>
      </Divider>
      {filteredNotes.map(note => (
        <NoteEditorBox key={note.id}>
          <NoteEditor element={note} gutter={false} />
        </NoteEditorBox>
      ))}
      {/* Show created note or create prompt */}
      {createdNote ? (
        <NoteEditorBox>
          <NoteEditor style={{ flex: 1 }} element={createdNote} gutter={false} />
        </NoteEditorBox>
      ) : !hasNote ? (
        <NoteEditorBox>
          <div
            style={{ cursor: 'pointer', padding: 4 }}
            onClick={handleCreateNote}
          >
            <Typography variant="caption" color="textSecondary">
              Add a note...
            </Typography>
          </div>
        </NoteEditorBox>
      ) : null}

      <Divider>
        <Typography variant="caption">Documents</Typography>
      </Divider>
      {isLoading ? <LinearProgress /> : null}
      {entities.map(doc => (
        <Fragment key={doc.id}>
          <EntityContent key={doc.id} entity={doc} plugins={plugins} />
          <Button disabled={isDeleting} color="error" onClick={() => deleteEntity({ entity: doc })}>
            Delete
          </Button>
        </Fragment>
      ))}
      <UploadStoredEntity
        style={{ flex: 1 }}
        parentId={entity.id}
        contentHint="MusicXML or PDF"
        isPrivate={true}
        accept={mimeTypeAccept}
      />
    </VBox>
  )
}
